import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../api/http";
import { ScoreRing } from "../components/SeoScoreCard";
import { parseApiError } from "../utils/apiErrors";

function scoreColor(score) {
  if (score >= 80) return "var(--success)";
  if (score >= 60) return "#84cc16";
  if (score >= 40) return "var(--warning)";
  return "var(--danger)";
}

export default function EmbedWidget() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const targetUrl = (params.get("url") || "").trim();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [lastGood, setLastGood] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      if (!targetUrl) {
        setError("Missing url query parameter.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const { data } = await apiGet("/api/seo/widget-score", { url: targetUrl });
        if (!isMounted) return;
        setPayload(data);
        setLastGood(data);
      } catch (err) {
        if (!isMounted) return;
        setError(parseApiError(err, "Unable to load widget score"));
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [targetUrl]);

  const display = payload || lastGood;
  const seoScore = Number(display?.seoScore ?? 0);
  const scoreTextColor = scoreColor(seoScore);
  const brandUrl = window.location.origin;

  return (
    <div
      style={{
        margin: 0,
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        background: "transparent",
        padding: 10,
      }}
    >
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: 12,
          background: "var(--card-bg, #ffffff)",
          padding: 14,
          minWidth: 280,
          maxWidth: 420,
        }}
      >
        {loading ? <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading SEO score...</div> : null}

        {!loading && error && !display ? (
          <div style={{ color: "var(--danger)", fontSize: 13 }}>
            {error}
            <div style={{ marginTop: 6, color: "var(--text-muted)" }}>Try again shortly.</div>
          </div>
        ) : null}

        {!loading && display ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ScoreRing score={seoScore} size={78} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: scoreTextColor, lineHeight: 1.2 }}>
                Live SEO snapshot
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                for {display.url || targetUrl}
              </div>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                My SEO Score: {seoScore}/100 — powered by{" "}
                <a href={brandUrl} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>
                  Nova SEO
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
